import * as React from "react"
import { Flex, Box, Text, Progress, Image, Link, Avatar, Stack, Tag } from "@chakra-ui/react"
import dayjs from "dayjs"
import { Link as RLink } from "react-router-dom"
import relativeTime from "dayjs/plugin/relativeTime"
import { useQuery } from "react-query"

import { Table, Column } from "./Table"
import { humanize } from "../lib/helpers/text"
import { useOrgs } from "./providers/OrgProvider"
import { TABLE_PAGE_SIZE, TOPBAR_HEIGHT } from "../lib/config"
import { fetcher } from "../lib/fetcher"
import { BikeEntity, BikeState } from "../lib/types/bike"
import { AssignedTagDeclaration } from "../lib/types/customTag"
import { useFeatFlags } from "./providers/FeatFlagProvider"

dayjs.extend(relativeTime)

interface Order {
  order: string
  orderBy: string
}

interface Props {
  search: string
  bikeStates: BikeState[]
  bikeModels: string[]
  bikeTags: string[]
  requiresRefresh: number
}

interface BikesResponse {
  results: BikeEntity[]
  count: number
}

export const BikesTable = (props: Props) => {
  const { currentOrgs } = useOrgs()
  const { tags, bike_sn_as_name_extension } = useFeatFlags()
  const [order, setOrder] = React.useState<Order>({
    order: "-",
    orderBy: "last_cellular_update",
  })
  const [page, setPage] = React.useState<number>(1)

  const bikeStateQuery = props.bikeStates.reduce((acc, s) => acc + `&state=${s}`, "")
  const bikeModelQuery = props.bikeModels.reduce((acc, s) => acc + `&model=${s}`, "")
  const bikeTagQuery = props.bikeTags.reduce((acc, s) => acc + `&tag=${s}`, "")

  const currentOrgsUuids = currentOrgs.map(({ uuid }) => uuid)

  const fields = `&fields=description,bicycle_model_name,last_cellular_update,image_url,name,serial_number,state,state_of_charge,uuid,organization,tags`

  const url = `bicycles/?page_size=${TABLE_PAGE_SIZE}&ordering=${
    order.order + order.orderBy
  }&organizations=${currentOrgsUuids.join()}&query=${
    props.search
  }${bikeStateQuery}${bikeModelQuery}${bikeTagQuery}&include_state=true`

  const getBikes = React.useCallback(
    (currentPage: number) => fetcher(url + fields + `&page=${currentPage}`),
    [url, fields],
  )

  const { isLoading, data, refetch } = useQuery<BikesResponse, [string, number]>([url, page], () =>
    getBikes(page),
  )
  const bikes = data?.results || []

  const handleSetOrder = (order: Order) => {
    setOrder(order)
    setPage(1)
  }

  React.useEffect(() => {
    props.requiresRefresh > 0 && refetch()
  }, [props.requiresRefresh, refetch])

  return (
    <Box px={6} pb={6} overflowY="auto" maxHeight={`calc(100vh - ${TOPBAR_HEIGHT})`}>
      <Table
        isLoading={isLoading}
        data={bikes}
        count={data?.count || 0}
        noData="No assets"
        page={page}
        pageSize={TABLE_PAGE_SIZE}
        sort={order}
        onSort={handleSetOrder}
        setPage={setPage}
      >
        <Column<BikeEntity>
          sortKey="name"
          header="Asset"
          row={(bike) => (
            <Flex align="center">
              {bike.image_url && <Image src={bike.image_url} h="20px" w="30px" mr={2} objectFit="contain" />}
              <Box>
                <Link
                  as={RLink}
                  to={`/assets/${bike.uuid}`}
                  fontSize="0.8rem"
                  fontWeight="600"
                  whiteSpace="nowrap"
                >
                  {bike.name}
                  {bike.serial_number &&
                    bike_sn_as_name_extension?.visible &&
                    bike.name.toUpperCase() !== bike.serial_number.toUpperCase() &&
                    `.${bike.serial_number}`}
                  {/* shows serial number explicitly */}
                  {/* {bike.serial_number && bike_sn_as_name_extension?.visible
                    && bike.name.toUpperCase() !== bike.serial_number.toUpperCase() && (
                    <span style={{ fontSize: "0.6rem", marginLeft: 4, fontWeight: "lighter" }}>
                      {bike.serial_number}
                    </span>
                  )} */}
                </Link>
                {bike.description ? (
                  <Text fontSize="0.6rem" whiteSpace="nowrap">
                    {bike.description}
                  </Text>
                ) : (
                  <Text fontSize="0.6rem" whiteSpace="nowrap">
                    {bike.bicycle_model_name || "Uncategorized"}
                  </Text>
                )}
              </Box>
            </Flex>
          )}
        />
        {currentOrgs.length > 1 ? (
          <Column<BikeEntity>
            sortKey="organization"
            header="Organization"
            row={(bike) => {
              const org = currentOrgs.find(({ url }) => url === bike.organization)!
              if (!org) return null
              return (
                <Flex align="center">
                  <Avatar mr={2} name={org.name} src={org.icon} size="xs" />

                  <Text fontSize="0.6rem" whiteSpace="nowrap">
                    {org.name}
                  </Text>
                </Flex>
              )
            }}
          />
        ) : undefined}
        <Column<BikeEntity>
          sortKey="state_of_charge"
          header="Battery level"
          row={(bike) => (
            <Flex align="center">
              <Text fontSize="0.6rem">{Math.ceil(bike.state_of_charge || 0)}%</Text>
              <Progress
                size="sm"
                ml={2}
                h="5px"
                borderRadius="md"
                colorScheme={
                  !bike.state_of_charge || bike.state_of_charge < 10
                    ? "red"
                    : bike.state_of_charge < 50
                    ? "orange"
                    : "green"
                }
                w="120px"
                value={bike.state_of_charge || 0}
              />
            </Flex>
          )}
        />
        <Column<BikeEntity>
          sortKey="last_cellular_update"
          header="Last update"
          maxW={200}
          row={(bike) => (
            <Text fontSize="0.8rem" color="gray.700">
              {bike.last_cellular_update ? dayjs(bike.last_cellular_update).fromNow() : "N/A"}
            </Text>
          )}
        />
        {tags.visible ? (
          <Column<BikeEntity>
            header="Tags"
            row={(bike) => {
              if (!bike || !bike.tags) return null
              return (
                <Stack direction="row">
                  {bike.tags.map((tag: AssignedTagDeclaration) => (
                    <Tag key={tag.uuid} bg={tag.tag_declaration_color} size="sm">
                      {tag.tag_declaration_name}
                    </Tag>
                  ))}
                </Stack>
              )
            }}
          />
        ) : undefined}
        <Column<BikeEntity>
          sortKey="state"
          header="State"
          maxW={200}
          row={(bike) => (
            <Text fontSize="0.8rem" color="gray.700">
              {humanize(bike.state)}
            </Text>
          )}
        />
      </Table>
    </Box>
  )
}
